/**
 * «Por que este aparelho está calado?» — a corrente inteira conferida, elo por elo.
 *
 * Não existe ping para este rastreador: ele fala quando quer, e o servidor não o alcança sem uma
 * conexão aberta. O que responde a pergunta de verdade é confrontar o que CADA elo sabe. O Traccar
 * viu e o bHunter não? O forward está quebrado. Nenhum dos dois viu? O problema é antes, no
 * aparelho ou na rede dele. Só o bHunter tem posição antiga e o Traccar diz offline? Ele falou e
 * parou.
 *
 * Sem isto, «silencioso» é um rótulo sem explicação — e quem lê não sabe se espera ou se age.
 */
import { AlertTriangle, Check, Stethoscope, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { desdeHumano } from '@/components/crud/SinalRastreio';

type Elo = { nome: string; ok: boolean; detalhe: string | null };
type Diagnostico = {
  unique_id: string;
  elos: Elo[];
  veredito: string;
  bhunter: { total_posicoes: number; ultima_em: string | null; silencio_seg: number | null };
  traccar: Record<string, unknown>;
  /** O servidor ouviu há pouco, mas a posição é velha: tem rede de celular e não tem céu. */
  sem_gps_com_rede: boolean;
};

export function PainelDiagnostico({ rastreadorId }: { rastreadorId: string }) {
  const [d, setD] = useState<Diagnostico | null>(null);
  const [rodando, setRodando] = useState(false);

  async function rodar() {
    setRodando(true);
    const r = await api.get<Diagnostico>(`/cadastro/rastreadores/${rastreadorId}/diagnostico`);
    setRodando(false);
    if (!r.ok) return void toast.error(r.erro);
    setD(r.dados);
  }

  const traccarIndisponivel = d?.traccar?.indisponivel === true;
  const statusTraccar = d?.traccar?.status as string | undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Stethoscope className="size-4 text-accent" />
          Diagnóstico
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Confere a corrente inteira — chip, APN, servidor, registro, instalação — e diz onde ela
          está partida.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Button variant="secundario" className="self-start" disabled={rodando} onClick={() => void rodar()}>
          {rodando ? <Spinner /> : <Stethoscope />} Conferir agora
        </Button>

        {d && (
          <>
            <div className="rounded-md border border-border bg-surface-2 px-3 py-2">
              <span className="tipo-rotulo">Veredito</span>
              <p className="text-[15px] text-text">{d.veredito}</p>
            </div>

            <div className="flex flex-col">
              {d.elos.map((e) => (
                <div key={e.nome} className="flex items-center gap-3 border-t border-border py-2 first:border-t-0">
                  {e.ok ? (
                    <Check className="size-4 shrink-0 text-success" />
                  ) : (
                    <X className="size-4 shrink-0 text-warning" />
                  )}
                  <span className="flex-1 text-[15px] text-text">{e.nome}</span>
                  {e.detalhe && (
                    <span className="tipo-dado text-text-tertiary">{e.detalhe}</span>
                  )}
                </div>
              ))}
            </div>

            <dl className="flex flex-wrap gap-x-8 gap-y-2 border-t border-border pt-3">
              <div>
                <dt className="tipo-rotulo">Posições no bHunter</dt>
                <dd className="tipo-dado text-text">
                  {d.bhunter.total_posicoes.toLocaleString('pt-BR')}
                  {d.bhunter.silencio_seg != null && (
                    <span className="text-text-tertiary">
                      {' '}
                      · última {desdeHumano(d.bhunter.silencio_seg)}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="tipo-rotulo">Conexão no servidor</dt>
                <dd className="tipo-dado text-text">
                  {traccarIndisponivel ? (
                    <span className="text-warning">não consegui perguntar</span>
                  ) : (
                    (statusTraccar ?? '—')
                  )}
                </dd>
              </div>
            </dl>

            {traccarIndisponivel && (
              <p className="tipo-corpo-pequeno flex items-start gap-1.5 text-warning">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                Sem resposta do servidor de rastreamento. Isso NÃO diz nada sobre o aparelho — diz
                que este diagnóstico está incompleto.
              </p>
            )}

            {d.sem_gps_com_rede && (
              <p className="tipo-corpo-pequeno flex items-start gap-1.5 text-warning">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                O aparelho conectou há pouco reenviando uma posição antiga: tem rede de celular e
                não está conseguindo fix de GPS. A rede atravessa parede; o GPS não. Leve-o para
                onde veja o céu.
              </p>
            )}

            {!d.sem_gps_com_rede && statusTraccar === 'offline' && d.bhunter.total_posicoes > 0 && (
              <p className="tipo-corpo-pequeno text-text-secondary">
                O aparelho já conversou com o servidor e não está conectado agora. Com o veículo
                desligado ou sem cobertura, é esperado. Se ele está rodando, o comando de{' '}
                <Chip className="border-accent/40 text-accent">modo GPRS</Chip> pode não ter sido
                aplicado — reenvie o terceiro SMS.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
